// cva is used to manage className variants dynamically, making styling components with different states or themes more efficient and reusable.
import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentProps, forwardRef } from 'react';
// Slot merges its props onto its immediate child.
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

import { mergeClassNames } from '@lib/utils';

// Setting different variants for the button component.
// We can customize this further and by adding more variants in the future.
const buttonVariants = cva(
  'w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        danger: 'bg-danger text-white hover:bg-danger/80',
        outline:
          'border border-gray-200 hover:border-transparent bg-transparent hover:bg-primary hover:text-white',
        link: 'font-medium text-foreground/80 hover:text-foreground active:text-foreground underline-offset-4 hover:underline',
        ghost: 'hover:bg-primary/5 hover:text-black/60',
        accent: 'bg-accent text-white hover:bg-accent/80',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

interface ButtonProps
  extends ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant,
      size,
      asChild = false,
      loading,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const isDisabled = loading || disabled;

    return (
      <Comp
        ref={ref}
        className={mergeClassNames(
          'relative',
          buttonVariants({ variant, size, className }),
          isDisabled && 'pointer-events-none',
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <div className='size-full flex-center'>
            <Loader2 className='size-6 object-contain animate-spin absolute' />
            <div className={clsx(loading && 'opacity-0')}>{children}</div>
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, type ButtonProps, buttonVariants };
